import React, { useEffect, useState, useCallback } from 'react';
import DailyIframe from "@daily-co/daily-js";
import { DailyProvider, useDaily, useParticipantIds, DailyAudio } from "@daily-co/daily-react";
import { Box, Typography, Button } from "@mui/material";
import AvatarVideo from "../avatarInterface/avatarVideo";
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useSearchParams } from 'react-router-dom';

const BoardAvatarFrameContent: React.FC = () => {
    const daily = useDaily();
    const remoteParticipantIds = useParticipantIds({ filter: "remote" });
    const [hasJoined, setHasJoined] = useState(false);

    // Read params from URL
    const [searchParams] = useSearchParams();
    const conversationUrl = searchParams.get('url');
    const memberName = searchParams.get('name') || 'Member';
    const role = searchParams.get('role') || '';

    // Internal Mic State (controlled by parent window)
    const [isMicEnabled, setIsMicEnabled] = useState(false);

    // Audio output muted state (for autoplay policy)
    const [audioMuted, setAudioMuted] = useState(true);

    // Handle audio play failure (browser autoplay policy)
    const handlePlayFailed = useCallback((e: any) => {
        console.log(`[${memberName}] Audio play failed:`, e);
        setAudioMuted(true); // Keep muted, user needs to click to unmute
    }, [memberName]);

    // Try to unmute audio after user gesture
    const handleUnmute = useCallback(() => {
        console.log(`[${memberName}] User clicked unmute`);
        setAudioMuted(false);
    }, [memberName]);

    // Listen for messages from parent
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Verify origin if needed, but for same-domain it's fine
            if (event.data && event.data.type === 'SET_MIC') {
                console.log(`[${memberName}] Received SET_MIC:`, event.data.enabled);
                setIsMicEnabled(event.data.enabled);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [memberName]);

    // Join Call
    useEffect(() => {
        if (!daily || hasJoined || !conversationUrl) return;

        const joinCall = async () => {
                try {
                console.log(`[${memberName}] Joining conversation:`, conversationUrl);

                await daily.join({
                    url: conversationUrl,
                    startVideoOff: true,
                    startAudioOff: true, // Start muted
                });
                console.log(`[${memberName}] Successfully joined conversation`);
                setHasJoined(true);
            } catch (err) {
                console.error(`Error joining call for ${memberName}:`, err);
            }
        };        joinCall();
    }, [daily, conversationUrl, hasJoined, memberName]);

    // Sync mic state with Daily
    useEffect(() => {
        if (!daily || !hasJoined) return;
        console.log(`[${memberName}] Setting local audio to:`, isMicEnabled);
        daily.setLocalAudio(isMicEnabled);
    }, [daily, hasJoined, isMicEnabled, memberName]);

    if (!conversationUrl) return <Typography color="error">Missing URL</Typography>;

    return (
        <Box sx={{
            position: 'relative',
            width: '100vw',
            height: '100vh',
            bgcolor: '#000',
            overflow: 'hidden',
            border: isMicEnabled ? '4px solid #00ff00' : 'none', // Thicker border for visibility
            boxSizing: 'border-box'
        }}>
            {remoteParticipantIds.length > 0 ? (
                <AvatarVideo id={remoteParticipantIds[0]} />
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Typography variant="body2" color="gray">Connecting...</Typography>
                </Box>
            )}

            {/* Overlay Info */}
            <Box sx={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                zIndex: 10,
                backgroundColor: 'rgba(0,0,0,0.6)',
                padding: '4px 8px',
                borderRadius: 1
            }}>
                <Typography variant="subtitle2" color="white" fontWeight="bold">
                    {memberName}
                </Typography>
                <Typography variant="caption" color="lightgray">
                    {role}
                </Typography>
            </Box>

            {/* Mic Status Indicator */}
            <Box sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 10,
                color: isMicEnabled ? '#00ff00' : 'gray'
            }}>
                {isMicEnabled ? <MicIcon fontSize="small" /> : <MicOffIcon fontSize="small" />}
            </Box>

            {/* Audio Unmute Button (for browser autoplay policy) */}
            {audioMuted && remoteParticipantIds.length > 0 && (
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUnmute}
                        startIcon={<VolumeOffIcon />}
                        sx={{
                            bgcolor: 'rgba(0,0,0,0.8)',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' }
                        }}
                    >
                        Click to Enable Audio
                    </Button>
                </Box>
            )}

            {/* DailyAudio for receiving audio from remote participants */}
            {!audioMuted && <DailyAudio onPlayFailed={handlePlayFailed} />}
        </Box>
    );
};

const BoardAvatarFrame: React.FC = () => {
    // Single call object per iframe instance
    const [callObject] = useState(() => DailyIframe.createCallObject());

    useEffect(() => {
        return () => {
            callObject.destroy();
        };
    }, [callObject]);

    return (
        <DailyProvider callObject={callObject}>
            <BoardAvatarFrameContent />
        </DailyProvider>
    );
};

export default BoardAvatarFrame;
